import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { beginRequest, finishRequest, pendingRequest, rejectedRequest } from "@/lib/idempotency";
import { CollaboratorSelect } from "@/components/CollaboratorSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowDownCircle, ArrowUpCircle, RotateCcw, Search, SlidersHorizontal, AlertTriangle } from "lucide-react";

const CONFIG = {
  RETIRAR: { endpoint: "/movements/withdraw", title: "Retirar peça", color: "bg-rose-600 hover:bg-rose-500", icon: ArrowDownCircle, machine: true, verb: "Retirar" },
  ENTRADA: { endpoint: "/movements/entry", title: "Dar entrada", color: "bg-emerald-600 hover:bg-emerald-500", icon: ArrowUpCircle, verb: "Confirmar entrada" },
  DEVOLVER: { endpoint: "/movements/return", title: "Devolver peça", color: "bg-sky-600 hover:bg-sky-500", icon: RotateCcw, machine: true, verb: "Devolver" },
  AJUSTE: { endpoint: "/movements/adjust", title: "Ajustar estoque", color: "bg-amber-600 hover:bg-amber-500", icon: SlidersHorizontal, requireMotivo: true, adjust: true, verb: "Ajustar" },
};

function MovementDialog({ type, part, open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const cfg = CONFIG[type] || CONFIG.RETIRAR;
  const { endpoint, adjust, machine } = cfg;
  const userId = user?.id;
  const tenantId = user?.tenant_id;
  const partId = part.id;
  const currentQuantity = part.quantidade_atual;
  const initializedForm = useRef(null);
  const inFlight = useRef(false);
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [qtd, setQtd] = useState(1);
  const [machineId, setMachineId] = useState("");
  const [os, setOs] = useState("");
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  const [retiradoPorId, setRetiradoPorId] = useState("");
  const [retiradoPorNome, setRetiradoPorNome] = useState("");
  const [loading, setLoading] = useState(false);
  const [ruptura, setRuptura] = useState(null);

  useEffect(() => {
    if (!open || !machine) return;
    let active = true;
    setMachines([]);
    api.get("/machines").then(r => {
      if (active) setMachines(r.data);
    }).catch(() => {});
    return () => { active = false; };
  }, [open, machine, userId, tenantId]);

  useEffect(() => {
    if (!open) { initializedForm.current = null; return; }
    const identity = JSON.stringify([endpoint, adjust, partId, userId, tenantId]);
    // A stock refresh must not overwrite an edit or an uncertain request.
    if (initializedForm.current === identity) return;
    initializedForm.current = identity;
    const saved = pendingRequest({ id: userId, tenant_id: tenantId }, endpoint, partId)?.payload;
    setQtd(saved?.quantidade ?? (adjust ? currentQuantity : 1));
    setMachineId(saved?.machine_id || ""); setOs(saved?.ordem_servico || "");
    setMotivo(saved?.motivo || ""); setObs(saved?.observacao || ""); setRuptura(null);
    setRetiradoPorId(saved?.retirado_por_id || ""); setRetiradoPorNome("");
    if (saved) toast.info("Operação anterior sem confirmação. Confira os dados e tente novamente para recuperar o resultado.");
  }, [open, endpoint, adjust, partId, currentQuantity, userId, tenantId]);

  const submit = async () => {
    if (inFlight.current) return;
    if (!Number.isSafeInteger(Number(qtd)) || Number(qtd) < (cfg.adjust ? 0 : 1) || String(qtd).trim() === "") {
      toast.error("Informe uma quantidade inteira válida."); return;
    }
    if (cfg.requireMotivo && !motivo.trim()) { toast.error("Motivo é obrigatório."); return; }
    if (type === "RETIRAR" && !retiradoPorId) { toast.error("Informe quem está retirando a peça (Retirado por)."); return; }
    inFlight.current = true; setLoading(true);
    try {
      const payload = {
        part_id: part.id,
        quantidade: Number(qtd),
        machine_id: machineId || undefined,
        retirado_por_id: type === "RETIRAR" ? retiradoPorId : undefined,
        ordem_servico: os, motivo, observacao: obs,
      };
      const request = beginRequest(user, cfg.endpoint, payload, part.id);
      const { data } = await api.post(cfg.endpoint, request.payload, { headers: { "Idempotency-Key": request.key } });
      finishRequest(user, cfg.endpoint, part.id);
      toast.dismiss();
      toast.success(`${cfg.title} registrada com sucesso.`, {
        action: { label: "Nova busca", onClick: () => navigate("/operacional") },
      });
      if (type === "RETIRAR" && data.alerta_ruptura) {
        setRuptura(data.part?.explanation);
      } else {
        onOpenChange(false);
      }
      onDone && onDone(data);
    } catch (e) {
      if (rejectedRequest(e)) finishRequest(user, cfg.endpoint, part.id);
      toast.error(apiErr(e));
    } finally {
      inFlight.current = false; setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><cfg.icon className="h-6 w-6" /> {cfg.title}</DialogTitle>
          <DialogDescription className="text-slate-400">{part.descricao} · Estoque atual: <b className="text-slate-200">{part.quantidade_atual}</b></DialogDescription>
        </DialogHeader>

        {ruptura ? (
          <div className="rounded-lg border border-rose-800/60 bg-rose-950/40 p-4 space-y-2">
            <div className="flex items-center gap-2 text-rose-300 font-semibold"><AlertTriangle className="h-5 w-5" /> Risco de ruptura</div>
            <p className="text-sm text-rose-100">{ruptura.motivo}</p>
            <p className="text-sm text-rose-200 font-medium">Recomendação: {ruptura.recomendacao}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">{cfg.adjust ? "Quantidade física (valor final)" : "Quantidade"}</Label>
              <Input data-testid="input-movement-qty" type="number" min={cfg.adjust ? 0 : 1} value={qtd}
                onChange={(e) => setQtd(e.target.value)} className="mt-1 bg-slate-950 border-slate-700 text-lg font-mono h-12" />
            </div>
            {type === "RETIRAR" && (
              <div>
                <Label className="text-slate-300">Retirado por <span className="text-rose-400">*</span></Label>
                <CollaboratorSelect valueId={retiradoPorId} onSelect={(id, nome) => { setRetiradoPorId(id || ""); setRetiradoPorNome(nome || ""); }} />
              </div>
            )}
            {cfg.machine && (
              <div>
                <Label className="text-slate-300">Máquina de destino {type === "RETIRAR" ? "" : "(opcional)"}</Label>
                <Select value={machineId} onValueChange={setMachineId}>
                  <SelectTrigger data-testid="select-movement-machine" className="mt-1 bg-slate-950 border-slate-700 h-12"><SelectValue placeholder="Selecione a máquina" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {type === "RETIRAR" && (
              <div><Label className="text-slate-300">Ordem de serviço (opcional)</Label>
                <Input value={os} onChange={(e) => setOs(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" placeholder="OS-1234" /></div>
            )}
            <div>
              <Label className="text-slate-300">Motivo {cfg.requireMotivo && <span className="text-rose-400">*</span>}</Label>
              <Input data-testid="input-movement-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" placeholder="Motivo da movimentação" />
            </div>
            <div>
              <Label className="text-slate-300">Observação (opcional)</Label>
              <Textarea value={obs} onChange={(e) => setObs(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" rows={2} />
            </div>
          </div>
        )}

        <DialogFooter>
          {ruptura ? (
            <Button data-testid="btn-movement-close" onClick={() => onOpenChange(false)} className="bg-sky-700 hover:bg-sky-600">Entendi</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button data-testid="btn-movement-confirm" onClick={submit} disabled={loading} className={cfg.color}>{loading ? "Processando..." : cfg.verb}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PartActions({ part, onDone, size = "default", showConsult = true, onConsult }) {
  const { can } = useAuth();
  const [type, setType] = useState(null);
  const big = size === "big";
  const btn = big ? "h-16 text-lg flex-col gap-1" : "h-11";

  const canWithdraw = can("ALMOXARIFADO");
  const canEntry = can("ALMOXARIFADO");

  return (
    <>
      <div className={`grid ${showConsult ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-3`}>
        {canWithdraw && (
          <Button data-testid="btn-action-retirar" onClick={() => setType("RETIRAR")} className={`${btn} bg-rose-600 hover:bg-rose-500 font-bold`}>
            <ArrowDownCircle className="h-5 w-5" /> Retirar
          </Button>
        )}
        {canEntry && (
          <Button data-testid="btn-action-entrada" onClick={() => setType("ENTRADA")} className={`${btn} bg-emerald-600 hover:bg-emerald-500 font-bold`}>
            <ArrowUpCircle className="h-5 w-5" /> Dar Entrada
          </Button>
        )}
        {canEntry && (
          <Button data-testid="btn-action-ajustar" onClick={() => setType("AJUSTE")} className={`${btn} bg-amber-600 hover:bg-amber-500 font-bold`}>
            <SlidersHorizontal className="h-5 w-5" /> Ajustar
          </Button>
        )}
        {showConsult && onConsult && (
          <Button data-testid="btn-action-consultar" variant="secondary" onClick={onConsult} className={`${btn}`}>
            <Search className="h-5 w-5" /> Consultar
          </Button>
        )}
      </div>
      {type && <MovementDialog type={type} part={part} open={!!type} onOpenChange={(v) => !v && setType(null)} onDone={(d) => { onDone && onDone(d); }} />}
    </>
  );
}
